import _ from "lodash";
import React, { useMemo } from "react";
import { Organization } from "../../../../common/models";
import { useDataMapPendingCollectedPicIds } from "../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapCollectedPICIds } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import {
  CategoryDto,
  DataMapDto,
  PICGroupWithCategories,
} from "../../../../common/service/server/dto/datamap/DataMapDto";
import { UUIDString } from "../../../../common/service/server/Types";
import { CollectionCheckboxes } from "../../get-compliant/collection-groups/CollectionCheckboxes";
import {
  PIC_GROUP_COMMERCIAL_INFORMATION,
  PIC_GROUP_CONSUMER_COMMUNICATIONS,
  PIC_GROUP_CUSTOM,
  PIC_GROUP_FINANCIAL_INFORMATION,
  PIC_GROUP_HEALTH_DATA,
  PIC_GROUP_INFERENCES,
} from "../../../pages/get-compliant/data-map/GroupIcon";

const GDPR_GROUP_NAME = "GDPR Sensitive Data";

export type OrderingVariant = "default" | "employee";
export type DisplayPicGroup = PICGroupWithCategories & {
  columns?: Boolean;
  allowCustom?: Boolean;
};

const PIC_GROUP_SINGLE_COLUMN = [
  PIC_GROUP_COMMERCIAL_INFORMATION,
  PIC_GROUP_FINANCIAL_INFORMATION,
  PIC_GROUP_HEALTH_DATA,
  PIC_GROUP_CONSUMER_COMMUNICATIONS,
  PIC_GROUP_INFERENCES,
  PIC_GROUP_CUSTOM,
];

/**
 * Builds the ordered list of PIC groups and their categories for collection checkbox UIs.
 *
 * Categories are grouped by PIC group, sorted according to {@link OrderingVariant}, and filtered
 * to exclude deprecated groups, optionally hidden PIC keys, and the GDPR Sensitive Data group when
 * the organization does not have GDPR enabled.
 *
 */
export const useFilteredPicGroups = ({
  dataMap,
  org,
  orderingVariant = "default",
  hiddenPicKeys,
}: {
  dataMap: DataMapDto;
  org: Organization;
  orderingVariant?: OrderingVariant;
  hiddenPicKeys?: string[];
}): DisplayPicGroup[] => {
  const groupedCategories: Record<UUIDString, CategoryDto[]> = useMemo(() => {
    let filteredCategories = Object.values(dataMap?.categories ?? {});
    if (Array.isArray(hiddenPicKeys)) {
      filteredCategories = filteredCategories.filter((cat) => !hiddenPicKeys.includes(cat.key));
    }
    return _.groupBy(filteredCategories, "picGroupId");
  }, [dataMap]);

  const orderedGroupedCategories: Record<UUIDString, CategoryDto[]> = useMemo(() => {
    return _.transform(
      groupedCategories,
      (result, categories, key) => {
        result[key] =
          orderingVariant === "employee"
            ? _.sortBy(categories, ["picGroupEmployeeOrder", "picGroupName"])
            : _.sortBy(categories, ["picGroupOrder", "picGroupName"]);
      },
      {},
    );
  }, [groupedCategories, orderingVariant]);

  const isGdpr = org?.featureGdpr ?? false;
  const filteredGroups: Record<UUIDString, CategoryDto[]> = useMemo(
    () =>
      _.omitBy(
        orderedGroupedCategories,
        (cats) => cats[0].picGroupName === GDPR_GROUP_NAME && !isGdpr,
      ),
    [orderedGroupedCategories, isGdpr],
  );

  return _.sortBy(Object.values(dataMap.picGroups), [
    orderingVariant === "employee" ? "employeeOrder" : "order",
  ])
    .filter(({ deprecated }) => !deprecated)
    .map(({ id }) => {
      const categories = Array.isArray(filteredGroups[id]) ? filteredGroups[id] : [];
      const group = dataMap.picGroups[id];
      return {
        ...group,
        categories,
        columns: !PIC_GROUP_SINGLE_COLUMN.includes(group.key),
        // For the default PIC groups, we can infer it supports custom PICs based on
        // the group key, but we still leave this open in case we wish to support custom
        // PICs at the category level or we wish to display non-standard PIC groupings.
        allowCustom: group.key == "custom",
      };
    });
};

export const ConsumerDataCollectionCheckboxes: React.FC<{
  dataMap: DataMapDto;
  org: Organization;
  collectionGroup: CollectionGroupDetailsDto;
  onCollectionChange?: () => Promise<void>;
  orderingVariant: OrderingVariant;
  disabledCategoryIds?: UUIDString[];
  disabledTooltipFn?: (checked: boolean) => string;
  hiddenPicKeys?: string[];
}> = ({
  dataMap,
  org,
  collectionGroup,
  onCollectionChange,
  disabledCategoryIds,
  disabledTooltipFn,
  orderingVariant = "default",
  hiddenPicKeys,
}) => {
  const orgId = org.id;

  const filteredGroups = useFilteredPicGroups({
    dataMap,
    org,
    orderingVariant,
    hiddenPicKeys,
  });

  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: collectionGroup?.id });
  const pending = useDataMapPendingCollectedPicIds(dataMap, {
    collectionGroupId: collectionGroup?.id,
  });

  return (
    <CollectionCheckboxes
      group={collectionGroup}
      categoryGroups={filteredGroups}
      orgId={orgId}
      collected={collected}
      pending={pending}
      onChange={onCollectionChange}
      disabledCategoryIds={disabledCategoryIds}
      disabledTooltipFn={disabledTooltipFn}
      containerClassName="mb-xl ml-mdlg"
    />
  );
};
